import {Observable} from 'rxjs';
import {SimpleUnit} from '@core/interfaces/system/system-common';
import {DataSource} from '@mominsamir/ngx-smart-table/lib/lib/data-source/data-source';

/**
 * Group search/list item
 */
export interface AssetGroup {
    groupId: string;
    groupName: string;
    groupType: string;
}

/**
 * Group contextual information
 */
export interface GroupContext {
    groupId: string;
    groupName: string;
    groupDescription: string;
    groupType: 'SUBSTATION' | 'FEEDER' | 'WATER_TREATMENT_PLANT';
    locationAddress: string;
    totalFunctionalUnits: number;
    totalAssets: number;
    totalLinearLength?: number; // Optional, may not apply to all group types
    lengthUnit: SimpleUnit;
}

/**
 * Functional unit within a group
 */
export interface FunctionalUnit {
    functionalUnitId: string;
    functionalUnitName: string;
    assetCount: number;
    linearLength?: number;
    assets?: FunctionalUnitAsset[]; // Populated when expanded
}

/**
 * Asset within a functional unit
 */
export interface FunctionalUnitAsset {
    assetId: string;
    assetName: string;
    assetClassCode: string;
    assetType?: string;
}

/**
 * Reliability Block Diagram configuration
 */
export interface ReliabilityBlockDiagram {
    functionalUnits: RBDFunctionalUnit[];
}

export interface RBDFunctionalUnit {
    functionalUnitId: string;
    functionalUnitName: string;
    failureProbability: number;
    consequenceContribution: number;
    configuration: 'series' | 'parallel';
    position: {x: number; y: number}; // For rendering
    connections: string[]; // Array of functionalUnitIds this connects to
}

/**
 * Failure mode data
 */
export interface FailureMode {
    failureModeId: string;
    failureModeName: string;
    description: string;
    cause: string;
    relativeProbability: number;
    consequenceDescription: string;
    probability1yr: number;
    probability3yr: number;
    probability5yr: number;
    probability10yr: number;
    outageCount1yr: number;
    outageCount3yr: number;
    outageCount5yr: number;
    outageCount10yr: number;
    downtimeHours1yr: number;
    downtimeHours3yr: number;
    downtimeHours5yr: number;
    downtimeHours10yr: number;
    consequenceCost1yr: number;
    consequenceCost3yr: number;
    consequenceCost5yr: number;
    consequenceCost10yr: number;
}

export abstract class GroupsService {
    abstract searchGroups(workflowItemId: number, searchString: string): Observable<AssetGroup[]>;

    abstract getGroupContext(workflowItemId: number, groupId: string): Observable<GroupContext>;

    abstract getGroupAssets(workflowItemId: number, groupId: string): Observable<FunctionalUnit[]>;

    abstract getFunctionalUnitAssets(
        workflowItemId: number,
        groupId: string,
        functionalUnitId: string,
    ): Observable<FunctionalUnitAsset[]>;

    abstract getReliabilityBlockDiagram(workflowItemId: number, groupId: string): Observable<ReliabilityBlockDiagram>;

    abstract getFailureModes(workflowItemId: number, groupId: string): Observable<DataSource>;
}
